import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';
import { Garage } from 'src/app/modules/shared/models/garage';

import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import * as _ from 'lodash';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { TrafficArea } from 'src/app/modules/shared/models/traffic-area';
import { ToggleViewComponent } from '../../../modules/shared/components/toggle-view/toggle-view.component';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { LoggingService } from '@modules/shared/services/logging.service';

export class TrafficAreaForm extends TrafficArea {
    public allSelected = false;
    public someSelected = false;
    public subTitle: string;
    public toggle: boolean;
    public toggleIcon: string;
    public visible = true;
    public visibleGarages: any = {};
}

@Component({
    selector: 'app-garage',
    templateUrl: './garage.component.html',
    styleUrls: ['./garage.component.scss'],
    imports: [
        TranslateModule, MatIconModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule, 
        MatButtonModule, ToggleViewComponent, NgClass, MatCheckboxModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class GarageComponent implements OnInit, OnDestroy {
    private _appStateService = inject(AppStateService);
    private _elementRef = inject(ElementRef);
    private _loggingService = inject(LoggingService);


    @HostListener('document:click', ['$event']) componentClick(event) {
        if(!this._elementRef.nativeElement.contains(event.target)) {
            // Hide menu if click is outside component
            this.showMenu = false;
        }
    }

    public garages: Garage[] = [];
    public showMenu = false;
    public trafficAreas: TrafficAreaForm[];

    public topBarSelectionText: string;
    public trafficAreaTexts: any = {};
    public trafficAreaToggle: any = {};

    public toggleableOptions: any = {};
    public query = new UntypedFormControl();

    private _ngUnsubscribe$ = new Subject<void>();
    private _selectedGarageCodes: string[];


    constructor() { }

    ngOnInit() {
        this._appStateService.getGaragesState()
            .pipe(takeUntil(this._ngUnsubscribe$))
            .subscribe((garages: Garage[]) => {
            this.garages = garages;

            if (this.garages) {
                this.trafficAreas = _.chain(this.garages)
                    .groupBy('trafficAreaName')
                    .map((value, key) => new TrafficAreaForm({ name: key, garages: value }))
                    .sortBy((trafficArea) => trafficArea.name)
                    .value();

                if (this._selectedGarageCodes) {
                    this._updateGarageForm(this._selectedGarageCodes);
                }
            }
        });

        this._appStateService.getPropertyState('selectedGarageCodes')
            .pipe(takeUntil(this._ngUnsubscribe$))
            .subscribe((selectedGarageCodes: string[]) => {
                this._selectedGarageCodes = selectedGarageCodes;
                this._updateGarageForm(selectedGarageCodes);
            });
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    public updateFilter() {
        this._updateGarageForm(this._selectedGarageCodes);
    }

    public resetFilter() {
        this.query.setValue('');
        this._updateGarageForm(this._selectedGarageCodes);
    }

    public toggleMenu() {
        this.showMenu = !this.showMenu;
    }

    public toggleTrafficArea(event, trafficArea: TrafficAreaForm) {
        let garageCodes = _.map(trafficArea.garages, (g) => g.code);

        garageCodes = _.chain(trafficArea.garages)
            .filter(garage => trafficArea.visibleGarages[garage.code] === true)
            .map((g) => g.code)
            .value();

        if (trafficArea.someSelected) {
            this._appStateService.removeSelectedGarages(garageCodes);

            this._loggingService.logMetric('TrafficArea.Selected', 1, {
                'Category': 'GarageSelection',
                'Subcategory': 'Remove',
                'TrafficArea': trafficArea?.name || 'Unknown',
            });
        } else {
            this._appStateService.addSelectedGarages(garageCodes);

            this._loggingService.logMetric('TrafficArea.Selected', 1, {
                'Category': 'GarageSelection',
                'Subcategory': 'Add',
                'TrafficArea': trafficArea?.name || 'Unknown',
            });
        }
    }

    public toggleGarageIsSelected(event: MatCheckboxChange, filterOptionKey: string) {
        event.checked ? this._appStateService.addSelectedGarages([filterOptionKey]) : this._appStateService.removeSelectedGarages([filterOptionKey]);
    }

    protected trackByIndex(index: number): number {
        return index;
    }

    private _updateGarageForm(selectedGarageCodes: string[] = []) {

        // set visible-flag on trafficArea if match on areaName or garagedescription
        if (this.query.value) {
            _.each(this.trafficAreas, trafficArea => {
                const trafficAreaMatch = trafficArea.name.toLowerCase().indexOf(this.query.value.toLowerCase()) >= 0;

                let anyGarageMatch = false;
                _.each(trafficArea.garages, garage => {
                    const garageMatch = garage.description.toLowerCase().indexOf(this.query.value.toLowerCase()) >= 0;
                    trafficArea.visibleGarages[garage.code] = garageMatch || trafficAreaMatch;
                    anyGarageMatch = anyGarageMatch || trafficArea.visibleGarages[garage.code];
                });

                trafficArea.visible = trafficAreaMatch || anyGarageMatch;
            });
        } else {
            _.each(this.trafficAreas, trafficArea => {
                trafficArea.visible = true;
                _.each(trafficArea.garages, garage => {
 trafficArea.visibleGarages[garage.code] = true; 
});
            });
        }

        // update checkbox-state
        this.toggleableOptions = {};
        _.each(selectedGarageCodes, gc => {
            this.toggleableOptions[gc] = true;
        });

        // update top-bar title
        if (selectedGarageCodes.length === 0) {
            this.topBarSelectionText = _translate('Select garage');
        } else if (selectedGarageCodes.length === 1) {
            this.topBarSelectionText = _.chain(
                _.filter(this.garages, (g) => this._selectedGarageCodes.includes(g.code))
                    .map((garage) => garage.description)
                    .join(', ')
            ).value();

        } else {
            this.topBarSelectionText = _translate('Multiple selection');
        }

        _.each(this.trafficAreas, (trafficArea: TrafficAreaForm) => {
            const intersection = _.intersection(selectedGarageCodes, _.map(trafficArea.garages, (g) => g.code));
            const filteredGarages = _.filter(trafficArea.garages, garage => trafficArea.visibleGarages[garage.code]);
            trafficArea.allSelected = intersection.length >= filteredGarages.length;
            trafficArea.someSelected = intersection.length > 0;
            trafficArea.subTitle = intersection.length > 0 ? `(${intersection.length}/${filteredGarages.length})` : '';
            trafficArea.toggleIcon = trafficArea.allSelected ? '-' : '+';
        });
    }
}
